import { useEffect, useRef, useState } from 'react';
import type { OrientationOverride } from '../../types/product.types';

interface ModelViewerProps {
  src: string;
  alt?: string;
  style?: React.CSSProperties;
  /** When set, applied imperatively to the model-viewer element after each model load. */
  orientation?: OrientationOverride | null;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          alt?: string;
          ar?: boolean;
          'ar-modes'?: string;
          'ar-scale'?: string;
          'xr-environment'?: boolean;
          'environment-image'?: string;
          'tone-mapping'?: string;
          'shadow-intensity'?: string;
          'shadow-softness'?: string;
          'camera-controls'?: boolean;
        },
        HTMLElement
      >;
    }
  }
}

export function ModelViewer({ src, alt = '3D Model', style, orientation }: ModelViewerProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mvRef = useRef<HTMLElement | null>(null);
  const orientationRef = useRef(orientation);

  useEffect(() => {
    orientationRef.current = orientation;
  }, [orientation]);

  function applyOrientation(el: any) {
    const o = orientationRef.current ?? {};
    const rx = o.rx ?? 0;
    const ry = o.ry ?? 0;
    const rz = o.rz ?? 0;
    const tx = o.tx ?? 0;
    const ty = o.ty ?? 0;
    const tz = o.tz ?? 0;
    try { el.orientation = `${rx}deg ${ry}deg ${rz}deg`; } catch { /* ignore */ }
    try {
      el.cameraTarget = `${-tx}m ${-ty}m ${-tz}m`;
    } catch {
      el.setAttribute('camera-target', `${-tx}m ${-ty}m ${-tz}m`);
    }
  }

  useEffect(() => {
    // Load model-viewer web component from CDN if not already loaded
    if (!customElements.get('model-viewer')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js';
      script.integrity = 'sha384-ycxtgj7aPUpK6OvsYarejYrITzqdS2Q6xoKkgnKMFoCyS5lQhW2h0OPCeInjM6qi';
      script.crossOrigin = 'anonymous';
      script.onload = () => setIsLoaded(true);
      script.onerror = () => {
        setHasError(true);
        setErrorMessage('Failed to load 3D viewer library');
      };
      document.head.appendChild(script);
    } else {
      setIsLoaded(true);
    }
  }, []);

  // Re-apply orientation whenever the orientation prop changes (live slider updates).
  useEffect(() => {
    const el = mvRef.current as any;
    if (!el) return;
    applyOrientation(el);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation?.rx, orientation?.ry, orientation?.rz, orientation?.tx, orientation?.ty, orientation?.tz]);

  // Re-apply orientation after a new src loads (model reload).
  useEffect(() => {
    const el = mvRef.current as any;
    if (!el || !isLoaded) return;

    const handler = () => {
      // Double-rAF so model-viewer's own updateFraming() has run first, then apply.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        applyOrientation(el);
        setTimeout(() => applyOrientation(el), 200);
      }));
    };

    el.addEventListener('load', handler);
    // If already loaded (cached GLB)
    if ((el as any).model?.materials?.length > 0) handler();

    return () => el.removeEventListener('load', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, src]);

  const handleModelError = (event: any) => {
    const errorMsg = event?.detail?.message || 'Unknown error';
    console.error('ModelViewer error:', {
      error: errorMsg,
      url: src,
      eventType: event?.type,
      eventDetail: event?.detail,
    });
    setHasError(true);
    setErrorMessage(`Failed to load 3D model: ${errorMsg}. URL: ${src}`);
  };

  if (hasError) {
    return (
      <div style={{ 
        ...style, 
        padding: '20px', 
        textAlign: 'center', 
        minHeight: '400px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <div>
          <p style={{ color: '#d72c0d', fontWeight: '500' }}>Error loading 3D model</p>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            {errorMessage || 'Unable to display the 3D model'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={style}>
      {isLoaded ? (
        <model-viewer
          ref={(el) => { mvRef.current = el as HTMLElement | null; }}
          src={src}
          alt={alt}
          ar
          ar-modes="webxr scene-viewer quick-look"
          ar-scale="fixed"
          xr-environment
          environment-image="neutral"
          tone-mapping="neutral"
          shadow-intensity="0.6"
          shadow-softness="0.6"
          camera-controls
          style={{ width: '100%', height: '500px', minHeight: '400px' }}
          onError={handleModelError}
        />
      ) : (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          minHeight: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9'
        }}>
          <div>
            <p>Loading 3D viewer...</p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              This may take a few seconds
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
